import { AlertTriangle, ExternalLink, Lock, Loader2 } from "lucide-react";
import FeedbackButtons from "@/components/FeedbackButtons";

/**
 * Renders the top-5 preview: category labels visible, findings blurred,
 * exactly 2 conflicts shown in full (in coral).
 */
export default function AnswersPreview({
  questions,
  previewAnswers,
  visibleConflicts,
  loading,
  caseId,
  problemType,
}) {
  const top5 = questions.slice(0, 5);
  const previewById = new Map(
    (previewAnswers || []).map((a) => [a.question_id, a])
  );
  const conflictsByQ = new Map();
  (visibleConflicts || []).forEach((c) => {
    if (!conflictsByQ.has(c.question_id)) conflictsByQ.set(c.question_id, []);
    conflictsByQ.get(c.question_id).push(c);
  });

  return (
    <div className="space-y-5" data-testid="answers-preview">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
          Top 5 · answered
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-white">
          The 5 that move the decision
        </h2>
        <p className="mt-2 text-sm text-[#A0AAB2] max-w-2xl leading-relaxed">
          Each answer is organised into categories and cross-checked across
          sources. Conflicts are flagged in{" "}
          <span className="text-[#FF5252] font-medium">coral</span> — because
          knowing where sources disagree is worth more than pretending they don't.
        </p>
        {loading && (
          <div
            data-testid="answers-loading"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#35D07F]/30 bg-[#35D07F]/8 text-[#35D07F] text-xs px-3 py-1.5"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
            Searching sources and cross-checking (this can take up to 2 minutes)…
          </div>
        )}
      </div>

      <div className="space-y-4">
        {top5.map((q, qi) => {
          const preview = previewById.get(q.id);
          const conflicts = conflictsByQ.get(q.id) || [];
          const labels = preview?.category_labels || [];
          return (
            <div
              key={q.id}
              data-testid={`preview-answer-${qi}`}
              className="rounded-2xl border border-white/10 bg-[#12181A] p-6"
            >
              <div className="flex items-start gap-3">
                <span className="text-xs font-mono text-[#35D07F] pt-1">
                  {String(q.rank).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 text-white font-medium leading-relaxed text-[15px]">
                      {q.text}
                    </div>
                    {caseId && (
                      <FeedbackButtons
                        caseId={caseId}
                        itemType="answer"
                        itemId={q.id}
                        itemText={q.text}
                        themeLabel={q.theme_label}
                        problemType={problemType}
                      />
                    )}
                  </div>

                  {labels.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {labels.map((label, li) => (
                        <div
                          key={li}
                          className="rounded-lg border border-white/10 bg-[#0B0F10] p-3"
                          data-testid={`preview-category-${qi}-${li}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#35D07F]">
                              {label}
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-white/5 text-white/60 text-[10px] uppercase tracking-wider px-2 py-0.5">
                              <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />
                              gated
                            </span>
                          </div>
                          <div
                            className="mt-2 select-none text-[15px] text-white/70 blur-[6px] pointer-events-none leading-relaxed"
                            aria-hidden="true"
                          >
                            Aggregate demand from three research firms indicates a
                            compound growth trend materially above the sector
                            average, with the fastest-growing segment concentrated
                            in a specific geography.
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 text-xs text-white/40">
                      No findings available for this question yet.
                    </div>
                  )}

                  {conflicts.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {conflicts.map((c, ci) => (
                        <ConflictCard key={ci} conflict={c} idx={`${qi}-${ci}`} />
                      ))}
                    </div>
                  )}

                  {preview?.conflicts_count > conflicts.length && (
                    <div className="mt-3 text-xs text-[#FF5252]/80">
                      +{preview.conflicts_count - conflicts.length} more conflict(s) locked
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConflictCard({ conflict, idx }) {
  return (
    <div
      data-testid={`visible-conflict-${idx}`}
      className="rounded-lg border border-[#FF5252]/40 bg-[#FF5252]/[0.06] p-3"
    >
      <div className="flex items-center gap-2 text-[#FF5252] text-[11px] font-semibold uppercase tracking-[0.15em]">
        <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
        Sources disagree
      </div>
      <div className="mt-1 text-[15px] text-white/95 font-medium leading-relaxed">{conflict.point}</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ValueCell label="Value A" value={conflict.value_a} source={conflict.source_a} />
        <ValueCell label="Value B" value={conflict.value_b} source={conflict.source_b} />
      </div>
    </div>
  );
}

function ValueCell({ label, value, source }) {
  return (
    <div className="rounded-md border border-white/10 bg-[#0B0F10] p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-white/40">
        {label}
      </div>
      <div className="mt-1 text-[15px] text-white leading-relaxed">{value}</div>
      {source?.url ? (
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#35D07F] hover:underline"
        >
          <ExternalLink className="h-3 w-3" strokeWidth={2} />
          {source.publisher || source.title || source.url}
        </a>
      ) : source?.publisher ? (
        <div className="mt-2 text-[11px] text-white/50">{source.publisher}</div>
      ) : null}
    </div>
  );
}
